import { useRef } from 'react'

import {
  Box,
  Colors,
  type Dimensions,
  PopoverMenu,
  Text,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { F0Link } from '@factorialco/f0-react'

export const formatDuration = (milliseconds: number) => {
  const isNegative = milliseconds < 0
  const absTotalMinutes = Math.abs(milliseconds) / 60000
  let hours = Math.floor(absTotalMinutes / 60)
  let minutes = Math.round(absTotalMinutes % 60)
  const sign = isNegative ? '-' : ''

  // Handle rollover when minutes rounds up to 60
  if (minutes === 60) {
    hours += 1
    minutes = 0
  }

  if (hours === 0 && minutes === 0) return '0h'
  if (hours === 0) return `${sign}${minutes}m`
  if (minutes === 0) return `${sign}${hours}h`

  return `${sign}${hours}h ${minutes}m`
}

export const Element = ({
  title,
  value,
  paddingLeft,
}: {
  title: string
  value: string
  paddingLeft?: Dimensions
}) => {
  return (
    <Box flexDirection="row" justifyContent="spaceBetween" paddingLeft={paddingLeft}>
      <Text size="100" color="grey1000">
        {title}
      </Text>
      <Text size="100" color="grey1000">
        {value}
      </Text>
    </Box>
  )
}

interface HoursDisplayProps {
  title: string
  totalDuration: string
  children?: React.ReactNode
  triggerColor?: Colors
  triggerClassName?: string
  linkTo?: string
  hideBody?: boolean
}

const HoursDisplay = ({
  title,
  totalDuration,
  children,
  triggerColor = 'grey1000',
  triggerClassName,
  linkTo,
  hideBody = false,
}: HoursDisplayProps) => {
  // Timeout to allow to hover the popover menu and click on the external link.
  const mouseEnterTimeout = useRef<ReturnType<typeof setTimeout>>()
  const mouseLeaveTimeout = useRef<ReturnType<typeof setTimeout>>()
  const [isHovered, setIsHovered] = useToggle(false)

  const handleMouseEnter = () => {
    if (mouseEnterTimeout.current) clearTimeout(mouseEnterTimeout.current)
    if (mouseLeaveTimeout.current) clearTimeout(mouseLeaveTimeout.current)

    mouseEnterTimeout.current = setTimeout(() => {
      setIsHovered(true)()
      mouseEnterTimeout.current = undefined
    }, 150)
  }

  const handleMouseLeave = () => {
    if (mouseLeaveTimeout.current) clearTimeout(mouseLeaveTimeout.current)
    if (mouseEnterTimeout.current) clearTimeout(mouseEnterTimeout.current)

    mouseLeaveTimeout.current = setTimeout(() => {
      setIsHovered(false)()
      mouseLeaveTimeout.current = undefined
    }, 100)
  }

  return (
    <Box flexDirection="row" alignItems="center" gap="s4">
      <PopoverMenu type="dialog" open={isHovered}>
        <PopoverMenu.Trigger asChild>
          <Box
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{ cursor: 'default' }}
          >
            <Text size="100" color={triggerColor} className={triggerClassName} dottedUnderline>
              {totalDuration}
            </Text>
          </Box>
        </PopoverMenu.Trigger>
        <PopoverMenu.Content
          side="bottom"
          align="center"
          offset={8}
          paddingTop="s0"
          paddingBottom="s0"
          hideOutline
        >
          <Box onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <Box
              padding="s12"
              flexDirection="row"
              justifyContent="spaceBetween"
              alignItems="center"
              border={{ bottom: { color: 'grey400', width: 's1', style: 'solid' } }}
            >
              {!linkTo && (
                <Text size="200" weight="medium" color="grey1000">
                  {title}
                </Text>
              )}
              {linkTo && (
                <F0Link
                  href={linkTo}
                  target="_blank"
                  variant="link"
                  className="text-f1-icon-secondary"
                >
                  {title}
                </F0Link>
              )}
              <Box flexDirection="row" alignItems="center" gap="s8">
                <Text size="200" weight="medium" color="grey1000">
                  {totalDuration}
                </Text>
              </Box>
            </Box>

            {!hideBody && children && (
              <Box
                flexDirection="column"
                background="grey100"
                gap="s8"
                paddingY="s8"
                paddingX="s12"
              >
                {children}
              </Box>
            )}
          </Box>
        </PopoverMenu.Content>
      </PopoverMenu>
    </Box>
  )
}

export default HoursDisplay
